import React from 'react';
import { RouterState } from 'mobx-state-router';
import { flattenArray, isSome, getLoginRoute } from 'core/utils';

export default function flattenRoutes(rootStore, routes) {
	var flatRoutes = getFlatRoutes(rootStore, routes);
	return {
		routes: flatRoutes,
		routerMaps: getRouterMaps(flatRoutes),
	};
}

function getRouterMaps(flatRoutes) {
	var maps = {};
	flatRoutes.forEach(item => {
		maps[item.name] = item.component;
	});
	return maps;
}

function getFlatRoutes(rootStore, routes, parentOptions = []) {
	return flattenArray(
		routes.map(r => {
			return flatten(rootStore, r, parentOptions);
		})
	);
}

function flatten(rootStore, route, parentOptions = []) {
	var options = {
		layouts: [],
		pattern: route.pattern,
		authorization: route.authorization,
		isPrivate: route.isPrivate || false,
		parent: parentOptions.length > 0 ? parentOptions[parentOptions.length - 1].parent : null,
		beforeExit: route.beforeExit,
		beforeEnter: route.beforeEnter,
		onExit: route.onExit,
		onEnter: route.onEnter,
	};

	if (route.children && route.children.length > 0) {
		options.parent = route;
		options.layouts = append(options.layouts, route.component);

		var children = getFlatRoutes(rootStore, route.children, [...parentOptions, options]);
		return [...children];
	}

	// Support adding layout to leaf component (one that should be presented for that route)
	if (Array.isArray(route.component) && route.component.length > 1) {
		var [component] = route.component.splice(-1);
		options.layouts = [...options.layouts, ...route.component];
		route.component = component;
	}

	const checkOptions = [...parentOptions, options];
	route = applyLayoutOptions(route, checkOptions);

	route.isPrivate = checkOptions.reduce((prev, current) => prev || current.isPrivate, false);
	route.pattern = checkOptions.reduce((prev, current) => joinPaths(prev, current.pattern), '');

	route.beforeEnter = async (fromState, toState, routerStore) => {
		if (routerStore.isSilentTransition === true) return Promise.resolve();

		routerStore.rootStore.errorStore.internalException = null;
		for (let i = 0; i < checkOptions.length; i++) {
			const current = checkOptions[i];
			if (current.isPrivate) {
				if (!rootStore.authStore.isAuthenticated) {
					rootStore.authStore.setSignInRedirect(toState);
					const loginState = getLoginRoute(toState.params.appId || undefined);
					return Promise.reject(loginState);
				}
			}

			if (
				current.beforeEnter &&
				(fromState.routeName !== toState.routeName || !compareObjects(fromState.params, toState.params))
			) {
				try {
					await current.beforeEnter(fromState, toState, routerStore);
				} catch (ex) {
					if (ex instanceof RouterState === false) {
						routerStore.rootStore.errorStore.internalException = ex;
						return Promise.reject(new RouterState('master.error'));
					}
					return Promise.reject(ex);
				}
			}

			if (isSome(current.authorization) && !rootStore.authStore.hasPermission(current.authorization)) {
				return Promise.reject(new RouterState('unauthorized'));
			}
		}

		return Promise.resolve();
	};

	return [route];
}

function applyLayoutOptions(route, parentOptions) {
	var layouts = parentOptions.reduce((acc, current) => {
		return [...acc, current.layouts];
	}, []);
	layouts = flattenArray(layouts).reverse();
	layouts.forEach(w => {
		route.component = renderParentComponent({
			Parent: w,
			component: route.component,
		});
	});

	route.component = renderComponent(route.component);
	return route;
}

function renderComponent(Component, props = {}) {
	return <Component {...props} />;
}

function renderParentComponent({ Parent, component }) {
	return props => (
		<Parent
			{...props}
			render={renderProps => {
				return renderComponent(component, renderProps);
			}}
		/>
	);
}

function append(items, value) {
	if (value === null || value === undefined) {
		return items;
	}

	if (Array.isArray(value)) {
		items = [...items, ...value];
	} else {
		items.push(value);
	}
	return items;
}

function joinPaths(a, b) {
	a = a || '';
	b = b || '';
	if (b === '') return a;
	return removeEndingSlash(a) + '/' + removeStartingSlash(b);
}

function removeStartingSlash(str) {
	return str.replace(/^[\/]+/, '');
}

function removeEndingSlash(str) {
	return str.replace(/[\/]+$/, '');
}

function compareObjects(o1, o2) {
	for (var p in o1) {
		if (o1.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	for (var p in o2) {
		if (o2.hasOwnProperty(p)) {
			if (o1[p] !== o2[p]) {
				return false;
			}
		}
	}
	return true;
}



// WEBPACK FOOTER //
// ./src/core/utils/flattenRoutes.js